import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Button } from "react-bootstrap";
import moment from "moment";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { formattedDate } from "../../../utils/function";
import { adminGetUtmDetailsList } from "../../../config/service";

const SearchHeader = ({
  field,
  fieldLabel,
  columnSearchRef,
  onSearch,
  searchNonce,
}) => {
  const [localValue, setLocalValue] = useState(
    columnSearchRef.current[field] || "",
  );

  useEffect(() => {
    setLocalValue(columnSearchRef.current[field] || "");
  }, [searchNonce]);

  const handleSearch = () => {
    columnSearchRef.current[field] = localValue;
    onSearch();
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <strong>{fieldLabel}</strong>
      <div className="d-flex">
        <input
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search"
        />
        <button onClick={handleSearch}>🔍</button>
      </div>
    </div>
  );
};

const toIsoDate = (dateStr) => {
  if (!dateStr) return "";

  // expecting DD/MM/YYYY
  const parsed = moment(dateStr, "DD/MM/YYYY", true);

  if (!parsed.isValid()) return "";

  return parsed.toISOString(); // 👉 2024-07-08T14:30:00.148Z
};

const UtmMeetingsReport = () => {
  const gridApi = useRef(null);
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;

  const initialSearchFields = {
    meetingId: "",
    meetingDate: "",
    doctorName: "",
    doctorSpecialty: "",
    patientName: "",
    utm: "",
  };

  const columnSearchRef = useRef({ ...initialSearchFields });
  const [searchNonce, setSearchNonce] = useState(0);

  const handleSearchUpdate = () => {
    setCurrentPage(1);
    setSearchNonce((n) => n + 1);
  };

  const handleResetSearch = () => {
    columnSearchRef.current = { ...initialSearchFields };
    setSearchNonce((n) => n + 1);
    setCurrentPage(1);
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "meetingId",
        headerName: "Meeting ID",
        headerComponent: () => (
          <SearchHeader
            field="meetingId"
            fieldLabel="Meeting ID"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "meetingDate",
        headerName: "Meeting Date",
        valueFormatter: (p) => formattedDate(p.value),
        headerComponent: () => (
          <SearchHeader
            field="meetingDate"
            fieldLabel="Date"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "doctorName",
        headerName: "Doctor Name",
        headerComponent: () => (
          <SearchHeader
            field="doctorName"
            fieldLabel="Doctor"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "doctorSpecialty",
        headerName: "Doctor Specialty",
        headerComponent: () => (
          <SearchHeader
            field="doctorSpecialty"
            fieldLabel="Specialty"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "patientName",
        headerName: "Patient Name",
        headerComponent: () => (
          <SearchHeader
            field="patientName"
            fieldLabel="Patient"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
      {
        field: "utm",
        headerName: "UTM Identifier",
        headerComponent: () => (
          <SearchHeader
            field="utm"
            fieldLabel="UTM"
            columnSearchRef={columnSearchRef}
            onSearch={handleSearchUpdate}
            searchNonce={searchNonce}
          />
        ),
      },
    ],
    [searchNonce],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      editable: false,
    }),
    [],
  );

  const fetchUtmMeetings = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const payload = {
        page,
        perPage: itemsPerPage,
        searchFields: {
          ...columnSearchRef.current,
          meetingDate: toIsoDate(columnSearchRef.current.meetingDate),
        },
      };

      const response = await adminGetUtmDetailsList(payload);

      const rows = response.data.data.map((d) => ({
        meetingId: d.meetingId,
        meetingDate: d.meetingDate,
        doctorName: d.doctorName,
        doctorSpecialty: d.doctorSpecialty,
        patientName: d.patientName,
        utm: d.utmIdentifier,
      }));

      setRowData(rows);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUtmMeetings(currentPage);
  }, [currentPage, searchNonce]);

  const onGridReady = (params) => {
    gridApi.current = params.api;
  };

  const exportCurrentPage = () => {
    gridApi.current.exportDataAsCsv({
      fileName: `utm-meetings-${moment().format("DD-MM-YYYY-HH-mm")}.csv`,
    });
  };

  const exportAll = async () => {
    try {
      if (!gridApi.current) return;
      const response = await adminGetUtmDetailsList({
        page: 1,
        perPage: 1000000,
        searchFields: { meetingId: "", meetingDate: "", doctorName: "", doctorSpecialty: "", patientName: "", utm: "", },
      });

      const allRows = response.data.data.map((d) => ({
        meetingId: d.meetingId,
        meetingDate: d.meetingDate,
        doctorName: d.doctorName,
        doctorSpecialty: d.doctorSpecialty,
        patientName: d.patientName,
        utm: d.utmIdentifier,
      }));

      const prevData = rowData;
      gridApi.current.setRowData(allRows);

      gridApi.current.exportDataAsCsv({
        fileName: `utm-meetings-all-${moment().format("DD-MM-YYYY-HH-mm")}.csv`,
        allColumns: true,
      });

      gridApi.current.setRowData(prevData);
  } catch (err) {
    console.error(err);
      toast.error("Export failed");
  }
};

  return (
    <div className="user-mangement-page">
      <div className="container-fluid">
        <div className="ag-theme-alpine" style={{ height: 650 }}>
          <div className="mb-2 d-flex flex-row align-items-center">
            <Button onClick={exportCurrentPage} className="download_report">
              Export current page
            </Button>
            <Button onClick={exportAll} className="download_report ms-2">
              Export all data to CSV
            </Button>
            <Button className="ms-2 me-auto" onClick={handleResetSearch}>
              Clear search
            </Button>
          </div>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <RotatingLines width="64" strokeColor="grey" />
            </div>
          ) : (
            <>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={60}
                headerHeight={60}
                onGridReady={onGridReady}
              />

              {rowData.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  <Button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    Prev
                  </Button>

                  <span className="mx-3" style={{ lineHeight: "38px" }}>
                    Page {currentPage} of {Math.ceil(totalCount / itemsPerPage)}
                  </span>

                  <Button
                    disabled={
                      currentPage === Math.ceil(totalCount / itemsPerPage)
                    }
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UtmMeetingsReport;
